// WHAT: Clientseitiger Such-Service für globale Routen und Quick-Actions innerhalb von TracQ
// WHY:  Ermöglicht eine einheitliche Spotlight-/Command-Palette-Suche über Domains hinweg ohne Server-Roundtrips
// DOMAIN: core → services
// DEPENDENCIES: `SearchResult`-Typ aus Shared-Contexts, Window-Navigation, domänenspezifische Routen/Actions
// RISKS:
//   - NOTE: Neue Routen/Features müssen explizit in `searchableItems` ergänzt werden, sonst bleiben sie unsichtbar
//   - NOTE: Direkte Nutzung von `window.location.href` führt zu Full-Page-Reloads; SPA-Navigation könnte optional ergänzt werden
import { type SearchResult } from "@/shared/contexts";

// NOTE:
//   `searchableItems` dient als in-memory Index für die Suchfunktion und sollte
//   regelmäßig mit neuen Domains/Workspaces/Tools synchronisiert werden.
// Searchable routes and content
const searchableItems = [
  // Core Routes
  {
    description: "Main application dashboard",
    icon: "📊",
    id: "dashboard",
    keywords: ["dashboard", "main", "home", "overview"],
    path: "/dashboard",
    title: "Dashboard",
    type: "route" as const,
  },

  // Health & Oura
  {
    description: "Oura ring health data and analytics",
    icon: "💍",
    id: "oura",
    keywords: ["oura", "health", "sleep", "activity", "readiness", "heart rate", "stress"],
    path: "/nexus/health/oura",
    title: "Oura Health",
    type: "route" as const,
  },
  {
    description: "Health and wellness tracking",
    icon: "🏥",
    id: "health",
    keywords: ["health", "wellness", "fitness", "tracking"],
    path: "/health",
    title: "Health",
    type: "route" as const,
  },

  // Productivity
  {
    description: "Time tracking and productivity management",
    icon: "⏱️",
    id: "timetracq",
    keywords: ["time", "tracking", "productivity", "tasks", "planning"],
    path: "/timetracq",
    title: "TimeTracQ",
    type: "route" as const,
  },
  {
    description: "Personal development and goal setting",
    icon: "✨",
    id: "miracle-flow",
    keywords: ["miracle", "flow", "goals", "development", "personal"],
    path: "/productivity/miracle-flow",
    title: "Miracle Flow",
    type: "route" as const,
  },
  {
    description: "Task and reminder management",
    icon: "🔔",
    id: "reminders",
    keywords: ["reminders", "tasks", "todo", "notifications"],
    path: "/productivity/reminders",
    title: "Reminders",
    type: "route" as const,
  },

  // Admin & Scripts
  {
    description: "Development and automation scripts",
    icon: "🛠️",
    id: "scripts",
    keywords: ["scripts", "automation", "git", "cleanup", "development"],
    path: "/scripts",
    title: "Scripts",
    type: "route" as const,
  },
  {
    description: "Repository cleanup and maintenance",
    icon: "🧹",
    id: "git-cleanup",
    keywords: ["git", "cleanup", "repository", "maintenance", "branches"],
    path: "/git-cleanup",
    title: "Git Cleanup",
    type: "route" as const,
  },
  {
    description: "Project roadmap and planning",
    icon: "🗺️",
    id: "roadmap",
    keywords: ["roadmap", "planning", "projects", "timeline"],
    path: "/roadmap",
    title: "Roadmap",
    type: "route" as const,
  },

  // Workspaces
  {
    description: "Investment portfolio management",
    icon: "📈",
    id: "investments",
    keywords: ["investments", "portfolio", "stocks", "finance"],
    path: "/investments",
    title: "Investments",
    type: "route" as const,
  },
  {
    description: "Trading platform and analysis",
    icon: "📊",
    id: "trading",
    keywords: ["trading", "stocks", "analysis", "charts"],
    path: "/trading",
    title: "Trading",
    type: "route" as const,
  },
  {
    description: "Real estate investments and management",
    icon: "🏠",
    id: "real-estate",
    keywords: ["real estate", "property", "investments", "housing"],
    path: "/real-estate",
    title: "Real Estate",
    type: "route" as const,
  },
  {
    description: "Smart home automation and control",
    icon: "🏠",
    id: "smarthome",
    keywords: ["smart home", "automation", "switchbot", "iot"],
    path: "/smarthome",
    title: "Smart Home",
    type: "route" as const,
  },

  // Settings & Tools
  {
    description: "Application settings and configuration",
    icon: "⚙️",
    id: "settings",
    keywords: ["settings", "config", "preferences", "setup"],
    path: "/settings",
    title: "Settings",
    type: "route" as const,
  },
  {
    description: "AI assistant and chat interface",
    icon: "🤖",
    id: "chatgpt",
    keywords: ["chatgpt", "ai", "assistant", "chat", "openai"],
    path: "/llm/chatgpt",
    title: "ChatGPT",
    type: "route" as const,
  },

  // Quick Actions
  {
    action: "navigate-to-timetracq",
    description: "Create a new task in TimeTracQ",
    icon: "➕",
    id: "new-task",
    keywords: ["new", "task", "create", "add"],
    title: "Create New Task",
    type: "action" as const,
  },
  {
    action: "sync-oura",
    description: "Sync latest health data from Oura",
    icon: "🔄",
    id: "sync-oura",
    keywords: ["sync", "oura", "health", "data"],
    title: "Sync Oura Data",
    type: "action" as const,
  },
  {
    action: "navigate-to-git-cleanup",
    description: "Start repository cleanup process",
    icon: "🧹",
    id: "git-cleanup-action",
    keywords: ["git", "cleanup", "run", "start"],
    title: "Run Git Cleanup",
    type: "action" as const,
  },
];

/**
 * WHAT:
 *   Führt eine einfache, gewichtete Volltextsuche über `title`, `description` und `keywords`
 *   der statisch registrierten `searchableItems` aus und gibt die Top-10-Treffer zurück.
 *
 * WHY:
 *   Dient als zentrale Implementierung der globalen Such-/Command-Palette in der Core-Domain.
 *
 * INPUTS:
 *   - query: string — Vom Nutzer eingegebene Suchanfrage (mind. non-leer für Ergebnisse).
 *
 * OUTPUT:
 *   - Promise<SearchResult[]> — Bereits nach Score sortierte und auf 10 Einträge begrenzte Trefferliste.
 *
 * SIDE EFFECTS:
 *   - Treffer-`action` kann `window.location.href` setzen oder domänenspezifische Aktionen triggern.
 *
 * RISKS:
 *   - NOTE: Suchlogik ist case-insensitive, aber substring-basiert; kann zu sehr breiten Treffermengen führen.
 *   - NOTE: Die Relevanzgewichtung (10/5/3 Punkte) ist heuristisch und sollte bei Bedarf per UX-Feedback angepasst werden.
 *
 * @param query - Search query string
 *
 * @returns Promise resolving to array of search results
 *
 */
export const performSearch = (query: string): Promise<SearchResult[]> => {
  if (!query.trim()) {
    return Promise.resolve([]);
  }

  const searchTerm = query.toLowerCase();
  const results: SearchResult[] = [];

  // Search through all items
  for (const item of searchableItems) {
    const titleMatch = item.title.toLowerCase().includes(searchTerm);
    const descriptionMatch = item.description.toLowerCase().includes(searchTerm);
    const keywordMatch = item.keywords.some((keyword) =>
      keyword.toLowerCase().includes(searchTerm),
    );

    if (titleMatch || descriptionMatch || keywordMatch) {
      // Calculate relevance score
      let score = 0;
      if (titleMatch) {
        score += 10;
      }
      if (descriptionMatch) {
        score += 5;
      }
      if (keywordMatch) {
        score += 3;
      }

      results.push({
        action: () => {
          if (item.type === "route") {
            // Navigate to route
            globalThis.location.href = item.path;
          } else if (item.action) {
            // Execute action
            executeAction(item.action);
          }
        },
        description: item.description,
        icon: item.icon,
        id: item.id,
        score,
        title: item.title,
        type: item.type,
      });
    }
  }

  // Sort by relevance score (highest first)
  results.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  // Return top 10 results
  return Promise.resolve(results.slice(0, 10));
};

const executeAction = (action: string) => {
  switch (action) {
    case "navigate-to-timetracq": {
      globalThis.location.href = "/timetracq";
      break;
    }
    case "navigate-to-git-cleanup": {
      globalThis.location.href = "/git-cleanup";
      break;
    }
    case "sync-oura": {
      // Reason: Oura sync functionality will be implemented via dedicated sync hook
      // This action placeholder is kept for future implementation
      break;
    }
    default:
  }
};
